// ./src/pages/private/EventPageEditorPage/blockKinds.ts
//
// Shared helpers for the page block editor: the palette of block types the CMS
// offers, and the mapping between a stored PageWidget and the editor's notion of
// a "block category + kind". Rendering a block to safe HTML for the preview lives
// here too so the editor and per-block cards render identically.

import { sanitizeHtml } from './sanitize';
import { marked } from 'marked';

import { selectedShareNetworks } from './shareTargets';

import { PageWidget, TextBlockKind, ImageLayout, WidgetType } from './blockTypes';
import { EVENT_GUEST_ROLE_LABELS, EVENT_RSVP_STATUS_LABELS, PEOPLE_BLOCK_ROLES } from './constants';
import { peopleFilterOf } from './resolve/pageData';
import { fundraisingOptions } from './resolve/causes';

/** The block families the v1 CMS supports. */
export type BlockCategory =
  | 'text'
  | 'image'
  | 'embed'
  | 'video'
  | 'weather'
  | 'map'
  | 'countdown'
  | 'schedule'
  | 'guests'
  | 'hosts'
  | 'highlights'
  | 'stats'
  | 'tickets'
  | 'quote'
  | 'cards'
  | 'pois'
  | 'accommodations'
  | 'signup'
  | 'poll'
  | 'tasks'
  | 'travel'
  | 'budget'
  | 'eventMap'
  | 'partners'
  | 'partnerPackages'
  | 'volunteerShifts'
  | 'artists'
  | 'prizeWinners'
  | 'gameScoreboard'
  | 'myRole'
  | 'raceResults'
  | 'contactUs'
  | 'lostFound'
  | 'guestbook'
  | 'divider'
  | 'links'
  | 'share'
  | 'audio'
  | 'organizerEvents'
  | 'organizerFooter'
  | 'donate';

export interface BlockKindInfo {
  category: BlockCategory;
  /** For text blocks, the authoring kind; undefined for image blocks. */
  textKind?: TextBlockKind;
  icon: string;
  title: string;
  description: string;
  /** Extra search synonyms so "photo"/"speaker"/"timer" find the right block. */
  keywords?: string;
}

/** The "add block" palette, in display order. */
export const BLOCK_PALETTE: BlockKindInfo[] = [
  { category: 'text', textKind: 'richtext', icon: 'TextCaption', title: 'Rich text', description: 'Formatted text — headings, lists, links, colours.', keywords: 'paragraph copy wysiwyg content write body' },
  { category: 'text', textKind: 'text', icon: 'AlignLeft', title: 'Plain text', description: 'A simple paragraph of text.', keywords: 'paragraph copy content write body' },
  { category: 'text', textKind: 'markdown', icon: 'Markdown', title: 'Markdown', description: 'Write with Markdown syntax.', keywords: 'md text content' },
  { category: 'text', textKind: 'html', icon: 'Code', title: 'HTML', description: 'Paste your own HTML.', keywords: 'code embed raw markup' },
  { category: 'image', icon: 'Photo', title: 'Images', description: 'One or more images — gallery or carousel.', keywords: 'photo photos picture pictures gallery carousel slideshow media' },
  { category: 'video', icon: 'PlayerPlay', title: 'Video', description: 'A YouTube / Vimeo link or an uploaded video file.', keywords: 'movie clip film youtube vimeo mp4 media play' },
  { category: 'embed', icon: 'Frame', title: 'Embed', description: 'Embed a YouTube video, map, or any iframe.', keywords: 'iframe widget embed code' },
  { category: 'weather', icon: 'CloudRain', title: 'Weather', description: "Live forecast for your event's location and date.", keywords: 'forecast temperature rain sun climate' },
  { category: 'map', icon: 'MapPin', title: 'Location map', description: "A map of your event's venue with its name and address.", keywords: 'directions venue address location place google maps' },
  { category: 'countdown', icon: 'Clock', title: 'Countdown', description: 'A live countdown to the moment your event begins.', keywords: 'timer clock days until starts' },
  { category: 'schedule', icon: 'CalendarEvent', title: 'Schedule', description: "Your event's agenda — its time slots, times and venues.", keywords: 'agenda timeline itinerary programme program lineup times' },
  { category: 'guests', icon: 'Users', title: 'Guests', description: 'Show your guest list — as avatar cards or a simple list.', keywords: 'attendees people rsvp list who attending avatars' },
  { category: 'hosts', icon: 'UserStar', title: 'Hosts & organizers', description: 'Feature your hosts, organizers, or speakers with roles.', keywords: 'speaker speakers organizer organiser team crew host presenters staff performers' },
  { category: 'highlights', icon: 'Award', title: 'Highlights', description: "Your event's highlight badges — amenities, features, and more.", keywords: 'badges amenities features perks tags chips' },
  { category: 'stats', icon: 'ChartBar', title: 'Stats', description: 'A row of big numbers — guests, speakers, sponsors…', keywords: 'numbers metrics counts figures statistics counters' },
  { category: 'tickets', icon: 'Ticket', title: 'Tickets', description: 'Your ticket tiers & add-ons — early-bird prices, member rates, availability.', keywords: 'ticket pricing price buy purchase admission passes early bird addons register' },
  { category: 'quote', icon: 'Quote', title: 'Quote', description: 'A quotation from a speaker or famous figure, with an optional author.', keywords: 'quotation testimonial saying pullquote citation speaker words blockquote' },
  { category: 'cards', icon: 'LayoutGrid', title: 'Cards', description: 'A grid of titled cards — icon, text, link & colours. Great for hotels, museums, sponsors…', keywords: 'card tiles grid boxes features hotels museums places sponsors links panels' },
  { category: 'pois', icon: 'MapPin2', title: 'Points of interest', description: "Your event's places — hotels, restaurants, museums & more, from your saved points of interest.", keywords: 'poi places hotels restaurants museums attractions venues nearby directory guide things to do accommodation' },
  { category: 'accommodations', icon: 'BuildingSkyscraper', title: 'Accommodations', description: 'Places to stay for your guests — hotels and rentals with prices, distance and a group booking code.', keywords: 'accommodation accommodations hotel hotels lodging stay rooms rental resort hostel where to stay booking' },
  { category: 'signup', icon: 'ClipboardList', title: 'Signup list', description: 'A list guests sign up for — dishes, activities, gifts, shifts, rooms, merch or anything else.', keywords: 'signup sign up sheet potluck volunteer bring assignments contributions supplies merch donations wishlist what to bring' },
  { category: 'poll', icon: 'MessageCircle', title: 'Polls & quizzes', description: 'Questions guests answer on your event website — single or multiple choice, free text, or a scored quiz.', keywords: 'poll polls quiz quizzes question questions survey vote voting feedback trivia choices answers icebreaker' },
  { category: 'tasks', icon: 'Checklist', title: 'Tasks & appointments', description: "Your event's to-dos and appointments — with schedule, owner and progress.", keywords: 'tasks appointments todo checklist schedule run of show prep planning agenda assignments' },
  { category: 'travel', icon: 'Plane', title: 'Travel & itinerary', description: "Your event's travel — flights, shuttles & trains, grouped by day, with routes and times.", keywords: 'travel itinerary flight flights shuttle bus train transport arrivals departures transfers getting there journey trip legs' },
  { category: 'budget', icon: 'Coin', title: 'Budget', description: "Your event's budget — expected costs by category, with totals. Optionally show paid vs remaining.", keywords: 'budget cost costs expense expenses money spend spending finance financial estimate estimates totals categories accounting' },
  { category: 'eventMap', icon: 'Map', title: 'Event map', description: "An interactive map of your event's on-site elements — stages, booths, washrooms — that guests tap to see what's on there.", keywords: 'map site plan festival grounds stage booth washroom restroom entrance first aid facilities interactive markers wayfinding' },
  { category: 'partners', icon: 'HeartHandshake', title: 'Partners & sponsors', description: 'Showcase your sponsors, partners & exhibitors — logos, names & links, by type, package, or hand-picked.', keywords: 'partner partners sponsor sponsors exhibitor exhibitors supporters backers vendors logos logo wall thanks' },
  { category: 'partnerPackages', icon: 'Package', title: 'Sponsorship packages', description: 'Sell sponsorships: the packages this event offers, grouped by type, with a button to apply. The other partners block shows who already signed.', keywords: 'sponsorship package packages tier tiers sponsor sell offer apply application exhibitor booth prospectus rate card become a sponsor' },
  { category: 'volunteerShifts', icon: 'HeartHandshake', title: 'Volunteer shifts', description: 'Recruit volunteers: the shifts you opened for sign-up, grouped by role, with when, where, places left and the reward — people pick shifts and sign up.', keywords: 'volunteer volunteers shift shifts sign up signup help helpers crew staff recruit rota roster marshal steward' },
  { category: 'donate', icon: 'Heart', title: 'Fundraising', description: 'The causes this event collects for: a Give button, progress towards the goal and recent supporters, each your choice.', keywords: 'donate donation donations give gift gifts charity cause causes fundraiser fundraising goal target thermometer progress raised campaign total supporters donors tip contribute' },
  { category: 'artists', icon: 'Microphone2', title: 'Artists / Lineup', description: "Your event's booked artists — photos, billing, set times & links, as a festival-poster grid or a time-sorted list.", keywords: 'artist artists lineup line up band bands performer performers act acts speaker speakers dj djs music festival poster bill headliner' },
  { category: 'prizeWinners', icon: 'Trophy', title: 'Prize winners', description: 'Announce your raffle / prize-draw winners — by round, with names and prizes. Shows a "coming soon" message before a round is drawn.', keywords: 'prize prizes winner winners draw draws drawing raffle lottery giveaway lucky door prize announcement results names' },
  { category: 'gameScoreboard', icon: 'Sparkles', title: 'Game scoreboard', description: 'Live standings for a party game (Mafia, Imposter…) — teams still in play and the feed of eliminated players with their revealed roles.', keywords: 'game games party mafia imposter werewolf scoreboard score standings eliminations eliminated players roles live results leaderboard' },
  { category: 'myRole', icon: 'DeviceGamepad2', title: 'My game', description: 'Each guest plays from here, privately on their phone — their bingo card, secret role, Secret Santa draw or networking match; updates live as rounds are dealt. Each guest sees only their own.', keywords: 'game games party mafia imposter werewolf my role secret private card assignment player identity live' },
  { category: 'raceResults', icon: 'Trophy', title: 'Race results', description: 'A live leaderboard for sport events — bib, participant, wave and finish time, fastest first.', keywords: 'race results leaderboard finish times timing bib runners marathon 5k 10k winners standings ranking sport' },
  { category: 'lostFound', icon: 'Backpack', title: 'Lost & found', description: 'Guests tell you what they lost, or what they found and are bringing to the desk — and can see what the desk holds. Who can use it is set on the Lost & found page.', keywords: 'lost found lost and found property missing item wallet phone keys found something lost something desk claim' },
  { category: 'contactUs', icon: 'Mail', title: 'Contact us', description: 'A form guests use to message you — question, RSVP change, dietary note. Choose who can send.', keywords: 'contact us message form ask question get in touch reach out email enquiry inquiry feedback support' },
  { category: 'guestbook', icon: 'MessageCircle', title: 'Guest book', description: 'A shared wall of messages — guests leave notes and reply to each other. Choose who can post.', keywords: 'guest book guestbook comments comment wall messages notes wishes replies discussion thread conversation say something' },
  { category: 'links', icon: 'Link', title: 'Link buttons', description: 'Buttons to another page, a block on this page, Register / Get tickets, My orders, Add to calendar — or any web address.', keywords: 'button buttons cta url registry waiver rsvp form page anchor scroll section jump register tickets orders calendar action' },
  { category: 'share', icon: 'Share', title: 'Share this event', description: 'Let guests pass the event on \u2014 social networks, a copy-link button and a QR code.', keywords: 'share sharing social facebook twitter x linkedin whatsapp telegram reddit email qr qrcode copy link spread invite tell a friend word of mouth' },
  { category: 'organizerEvents', icon: 'CalendarEvent', title: 'Organizer’s events', description: 'Your other upcoming events from your organizer page — all of them, or only this event’s channel. Repeating events show once, with their next date.', keywords: 'organizer events upcoming other related more events series channel calendar what else next season program lineup listing' },
  { category: 'organizerFooter', icon: 'Building', title: 'Organizer footer', description: 'Your organizer logo, name, website, social links and contact details from your organizer page. Shows on plans that include branding.', keywords: 'organizer footer branding logo brand company organization about contact website social links email phone address hosted by organized by' },
  { category: 'audio', icon: 'Music', title: 'Audio', description: 'Music or a sound guests can play \u2014 with an optional background track (rain, the ocean, a crowd).', keywords: 'audio music song sound soundtrack playlist play listen background ambience ambient rain ocean waves crowd mp3 track' },
  { category: 'divider', icon: 'SeparatorHorizontal', title: 'Divider', description: 'A line or some blank space between blocks.', keywords: 'separator spacer line rule gap break space' },
];

/**
 * Which of the shared styling controls actually change a given block, so the
 * editor only offers the ones that do something.
 *
 *  • align   — **EVERY block** (owner, 2026-09-27). Alignment is a STANDARD setting.
 *  • columns — only text (CSS column flow) and image galleries. Guests / stats
 *              have their own per-block "cards/per row" control instead, so offering
 *              this everywhere WOULD be a dead control.
 *
 * Every block still gets title/subtitle, colours, and visibility — those are
 * rendered once and apply to all.
 *
 * ⚠⚠ `align` USED TO BE AN ALLOWLIST OF EIGHT CATEGORIES, and it was wrong. The
 * reasoning behind it — "only blocks whose body flows with text alignment; grids,
 * cards and maps ignore it" — sounds right and is not: `BlockRenderer` sets
 * `textAlign` on the block WRAPPER, and CSS `text-align` **INHERITS**. So it always
 * moved the heading (eyebrow / title / subtitle, which every block has) and most body
 * text too. **Verified by rendering, not argued:** share, guests, partners, quote,
 * stats and tickets all changed their markup under `align: 'center'` while the editor
 * refused to offer the control. 28 of 36 blocks were hiding a setting that worked.
 * ⚠ The opposite mistake is the one to stay afraid of — offering a control that does
 * nothing — which is why `columns` stays narrow. ▶ `npm run check-block-editor` pins
 * both halves: align is offered everywhere AND the renderer still applies it.
 */
export const blockStyleCaps = (category: BlockCategory): { align: boolean; columns: boolean; font: boolean } => ({
  align: true,
  columns: category === 'text' || category === 'image',
  // A2e #84 — the block's own font and text size: every block with text, i.e. all but the divider (a rule, no words).
  font: category !== 'divider',
});

/** Derive a widget's editor category from its stored widgetType + config. */
export const categoryOf = (w: PageWidget): BlockCategory => {
  const wt = w.widgetType;
  if (wt === 'image' || wt === 'image_gallery' || wt === 'image_carousel') return 'image';
  if (wt === 'embed') return 'embed';
  if (wt === 'video') return 'video';
  if (wt === 'weather') return 'weather';
  if (wt === 'map') return 'map';
  if (wt === 'countdown') return 'countdown';
  if (wt === 'agenda') return 'schedule';
  if (wt === 'guest_list') return w.config?.guestKind === 'hosts' ? 'hosts' : 'guests';
  if (wt === 'custom') {
    const ck = w.config?.customKind;
    if (ck === 'stats') return 'stats';
    if (ck === 'divider') return 'divider';
    if (ck === 'highlights') return 'highlights';
    if (ck === 'tickets') return 'tickets';
    if (ck === 'quote') return 'quote';
    if (ck === 'cards') return 'cards';
    if (ck === 'pois') return 'pois';
    if (ck === 'accommodations') return 'accommodations';
    if (ck === 'signup') return 'signup';
    if (ck === 'poll') return 'poll';
    if (ck === 'tasks') return 'tasks';
    if (ck === 'travel') return 'travel';
    if (ck === 'budget') return 'budget';
    if (ck === 'eventMap') return 'eventMap';
    if (ck === 'partners') return 'partners';
    if (ck === 'partnerPackages') return 'partnerPackages';
    if (ck === 'volunteerShifts') return 'volunteerShifts';
    if (ck === 'artists') return 'artists';
    if (ck === 'prizeWinners') return 'prizeWinners';
    if (ck === 'gameScoreboard') return 'gameScoreboard';
    if (ck === 'myRole') return 'myRole';
    if (ck === 'raceResults') return 'raceResults';
    if (ck === 'contactUs') return 'contactUs';
    if (ck === 'lostFound') return 'lostFound';
    if (ck === 'guestbook') return 'guestbook';
    if (ck === 'share') return 'share';
    if (ck === 'organizerEvents') return 'organizerEvents';
    if (ck === 'organizerFooter') return 'organizerFooter';
    if (ck === 'donate') return 'donate';
    // #204 — a stored `goal` block IS a Fundraising block (resolve/causes `fundraisingOptions`).
    if (ck === 'goal') return 'donate';
    if (ck === 'audio') {
      return 'audio';
    }
  }
  if (wt === 'links') return 'links';
  return 'text';
};

/** Derive the text authoring kind from the stored widget (defaults sensibly). */
export const textKindOf = (w: PageWidget): TextBlockKind => {
  const fromConfig = w.config?.textKind;
  if (fromConfig) return fromConfig;
  return w.widgetType === 'markdown' ? 'markdown' : 'richtext';
};

/** The widgetType to persist for a given text kind / image layout. */
export const widgetTypeForText = (kind: TextBlockKind): WidgetType => (kind === 'markdown' ? 'markdown' : 'html');
export const widgetTypeForImage = (layout: ImageLayout): WidgetType =>
  layout === 'carousel' ? 'image_carousel' : layout === 'gallery' ? 'image_gallery' : 'image';

/**
 * Normalise an embed URL to an embeddable form. YouTube + Vimeo watch links are
 * converted to their player URLs; everything else is used as-is (the author owns
 * the URL). Returns '' when nothing usable.
 */
export const toEmbedSrc = (raw: string | null | undefined): string => {
  const url = (raw ?? '').trim();
  if (!url) return '';
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/i);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return url;
};

const escapeHtml = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/**
 * Render a text block's body to sanitized HTML for the preview:
 *  • plain text → escaped, newlines become <br> inside paragraphs
 *  • markdown   → marked → sanitized
 *  • richtext / html → sanitized as-is
 */
export const textBlockToHtml = (w: PageWidget): string => {
  const kind = textKindOf(w);
  const raw = w.content ?? '';
  if (!raw.trim()) return '';
  if (kind === 'text') {
    const paras = raw
      .split(/\n{2,}/)
      .map((p) => `<p>${escapeHtml(p).replace(/\n/g, '<br/>')}</p>`)
      .join('');
    return sanitizeHtml(paras);
  }
  if (kind === 'markdown') {
    return sanitizeHtml(marked.parse(raw, { async: false }) as string);
  }
  // richtext + html are already HTML.
  return sanitizeHtml(raw);
};

/** A short, plain-text summary of a block for the list row. */
export const blockSummary = (w: PageWidget): string => {
  const cat = categoryOf(w);
  if (cat === 'links') {
    const n = w.config?.links?.length ?? 0;
    return n === 1 ? '1 link button' : `${n} link buttons`;
  }
  if (cat === 'schedule') return "Your event's agenda";
  if (cat === 'guests' || cat === 'hosts') {
    // What the block's filter says, in a line: the roles, then what narrows them.
    const f = peopleFilterOf(w.config, cat);
    const roles = f.roles.length === PEOPLE_BLOCK_ROLES.length ? 'Everyone' : f.roles.map((r) => EVENT_GUEST_ROLE_LABELS[r] ?? r).join(', ');
    const narrow = [
      f.ids.length > 0 ? `${f.ids.length} picked` : null,
      f.groups.filter(Boolean).length > 0 ? `${f.groups.filter(Boolean).length} group${f.groups.filter(Boolean).length === 1 ? '' : 's'}` : null,
      f.rsvp.length > 0 ? f.rsvp.map((s) => EVENT_RSVP_STATUS_LABELS[s] ?? s).join(' / ') : null,
    ].filter(Boolean);

    return [roles, ...narrow].join(' · ');
  }
  if (cat === 'highlights') return "Your event's highlights";
  if (cat === 'stats') {
    const n = w.config?.stats?.length ?? 0;
    return n === 1 ? '1 stat' : `${n} stats`;
  }
  if (cat === 'video') {
    const url = (w.config?.videoUrl ?? '').trim();
    return url ? url.replace(/^https?:\/\//, '').slice(0, 60) : 'No video yet';
  }
  if (cat === 'divider') return w.config?.dividerStyle === 'space' ? 'Blank space' : 'A divider line';
  if (cat === 'tickets') return 'Ticket tiers & add-ons';
  if (cat === 'quote') {
    const q = (w.config?.quoteText ?? '').trim();
    if (!q) return 'No quote yet';
    const author = (w.config?.quoteAuthor ?? '').trim();
    const text = q.length > 70 ? `${q.slice(0, 70)}…` : q;
    return author ? `“${text}” — ${author}` : `“${text}”`;
  }
  if (cat === 'cards') {
    const n = w.config?.cards?.length ?? 0;
    return n === 1 ? '1 card' : `${n} cards`;
  }
  if (cat === 'pois') {
    const src = w.config?.poiSource ?? 'all';
    if (src === 'selected') {
      const n = w.config?.poiIds?.length ?? 0;
      return n === 1 ? '1 point of interest' : `${n} points of interest`;
    }
    return src === 'category' ? 'Points of interest in a category' : 'All points of interest';
  }
  if (cat === 'accommodations') {
    const src = w.config?.accommodationSource ?? 'all';
    return src === 'category' ? 'Accommodations of one type' : 'All accommodations';
  }
  if (cat === 'signup') {
    const src = w.config?.signupSource ?? 'all';
    return src === 'category' ? 'Signup items of one type' : 'All signup items';
  }
  if (cat === 'poll') {
    const src = w.config?.pollSource ?? 'all';
    return src === 'category' ? 'Questions of one category' : 'All poll questions';
  }
  if (cat === 'tasks') {
    const src = w.config?.taskSource ?? 'all';
    return src === 'category' ? 'Tasks in a category' : 'All tasks & appointments';
  }
  if (cat === 'travel') {
    const src = w.config?.travelSource ?? 'all';
    if (src === 'selected') {
      const n = w.config?.travelSegmentIds?.length ?? 0;
      return n === 1 ? '1 travel leg' : `${n} travel legs`;
    }
    return 'All travel & itinerary';
  }
  if (cat === 'budget') {
    const src = w.config?.budgetSource ?? 'all';
    if (src === 'categories') {
      const n = w.config?.budgetCategoryIds?.length ?? 0;
      return n === 1 ? '1 budget category' : `${n} budget categories`;
    }
    return 'Full budget';
  }
  if (cat === 'eventMap') {
    const src = w.config?.eventMapSource ?? 'all';
    return src === 'category' ? 'Map elements in a category' : 'All on-site map elements';
  }
  if (cat === 'volunteerShifts') {
    if (w.config?.volunteerSource === 'roles') {
      const n = w.config?.volunteerRoleIds?.length ?? 0;

      return n === 1 ? 'Shifts of 1 role' : `Shifts of ${n} roles`;
    }

    return 'All open volunteer shifts';
  }
  if (cat === 'partnerPackages') {
    const src = w.config?.partnerPackageSource ?? 'all';

    if (src === 'selected') {
      const n = w.config?.partnerPackageIds?.length ?? 0;

      return n === 1 ? '1 selected package' : `${n} selected packages`;
    }

    return src === 'kind' ? 'Packages of one type' : 'All packages offered';
  }

  if (cat === 'partners') {
    const src = w.config?.partnerSource ?? 'all';
    if (src === 'selected') {
      const n = w.config?.partnerIds?.length ?? 0;
      return n === 1 ? '1 selected partner' : `${n} selected partners`;
    }
    return src === 'kind' ? 'Partners of one type' : src === 'package' ? 'Partners on one package' : 'All partners & sponsors';
  }
  if (cat === 'artists') {
    const src = w.config?.artistSource ?? 'all';
    if (src === 'picked') {
      const n = w.config?.artistIds?.length ?? 0;
      return n === 1 ? '1 selected artist' : `${n} selected artists`;
    }
    return src === 'billing' ? 'Artists of one billing tier' : 'All artists / lineup';
  }
  if (cat === 'prizeWinners') {
    return 'Published prize-draw winners';
  }
  if (cat === 'organizerEvents') {
    return w.config?.organizerEventsMode === 'channel' ? 'Upcoming events in this event’s channel' : 'All the organizer’s upcoming events';
  }
  if (cat === 'organizerFooter') {
    return 'Organizer logo, links and contact details';
  }
  if (cat === 'donate') {
    const o = fundraisingOptions(w.config);
    const which = o.causeId || o.firstCauseOnly ? 'One cause' : 'The event’s causes';

    return o.allowGifts ? `${which}, with a Give button` : `${which}, progress only`;
  }
  if (cat === 'gameScoreboard') {
    return 'Live party-game standings';
  }
  if (cat === 'myRole') {
    return 'Each guest’s private role';
  }
  if (cat === 'raceResults') {
    const src = w.config?.raceResultsSource ?? 'all';
    return src === 'group' ? 'Race results for one wave' : 'Full race leaderboard';
  }
  if (cat === 'audio') {
    const music = (w.config?.audioUrl ?? '').trim();
    const background = (w.config?.audioBackgroundUrl ?? '').trim();

    if (!music) {
      return 'No sound yet';
    }

    return background ? 'Music + a background track' : 'Music';
  }
  if (cat === 'share') {
    // ⚠ Counted through `selectedShareNetworks`, not `config.shareNetworks?.length ?? 0`:
    // a block the host has never opened has NO key and offers every network, so reading
    // the array's length directly would label a fully working block "no networks".
    const n = selectedShareNetworks(w.config?.shareNetworks).length;
    const parts = [n === 1 ? '1 network' : `${n} networks`];
    if (w.config?.shareShowCopy !== false) {
      parts.push('copy link');
    }
    if (w.config?.shareShowQr !== false) {
      parts.push('QR');
    }

    return parts.join(' · ');
  }
  if (cat === 'lostFound') {
    return 'Report lost and found items; the found list';
  }
  if (cat === 'contactUs') {
    return w.config?.contactAudience === 'recognized' ? 'Contact form (recognized guests only)' : 'Contact form (anyone)';
  }
  if (cat === 'guestbook') {
    if (w.config?.guestbookCanPost === false) {
      return 'Guest book (read-only)';
    }
    const aud = w.config?.guestbookAudience ?? 'all';
    return aud === 'rsvp_status' ? 'Guest book (by RSVP status)' : aud === 'group' ? 'Guest book (by group)' : 'Guest book (all guests can post)';
  }
  if (cat === 'map') return "Event venue on a map";
  if (cat === 'countdown') return 'Countdown to the event start';
  if (cat === 'weather') {
    return w.config?.weatherMode === 'forecast' ? `${w.config?.weatherDays ?? 5}-day forecast` : 'Current weather';
  }
  if (cat === 'embed') {
    const url = (w.config?.embedUrl ?? '').trim();
    if (url) return url.replace(/^https?:\/\//, '').slice(0, 60);
    return w.config?.embedHtml ? 'Embedded HTML' : 'Empty embed';
  }
  if (cat === 'image') {
    const n = w.config?.images?.length ?? 0;
    return n === 1 ? '1 image' : `${n} images`;
  }
  const html = textBlockToHtml(w);
  const text = html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  return text.length > 90 ? `${text.slice(0, 90)}…` : text || 'Empty text';
};

/** Human label for a block's kind (for the card + drawer header). */
export const blockKindLabel = (w: PageWidget): string => {
  const cat = categoryOf(w);
  if (cat === 'links') return 'Link buttons';
  if (cat === 'schedule') return 'Schedule';
  if (cat === 'guests') return 'Guests';
  if (cat === 'hosts') return 'Hosts & organizers';
  if (cat === 'highlights') return 'Highlights';
  if (cat === 'stats') return 'Stats';
  if (cat === 'tickets') return 'Tickets';
  if (cat === 'quote') return 'Quote';
  if (cat === 'cards') return 'Cards';
  if (cat === 'pois') return 'Points of interest';
  if (cat === 'accommodations') return 'Accommodations';
  if (cat === 'signup') return 'Signup list';
  if (cat === 'poll') return 'Polls & quizzes';
  if (cat === 'tasks') return 'Tasks & appointments';
  if (cat === 'travel') return 'Travel & itinerary';
  if (cat === 'budget') return 'Budget';
  if (cat === 'eventMap') return 'Event map';
  if (cat === 'partnerPackages') return 'Sponsorship packages';
  if (cat === 'volunteerShifts') return 'Volunteer shifts';
  if (cat === 'partners') return 'Partners & sponsors';
  if (cat === 'artists') return 'Artists / Lineup';
  if (cat === 'prizeWinners') return 'Prize winners';
  if (cat === 'gameScoreboard') return 'Game scoreboard';
  if (cat === 'myRole') return 'My role';
  if (cat === 'raceResults') return 'Race results';
  if (cat === 'contactUs') return 'Contact us';
  if (cat === 'lostFound') return 'Lost & found';
  if (cat === 'guestbook') return 'Guest book';
  if (cat === 'share') return 'Share this event';
  if (cat === 'organizerEvents') return 'Organizer’s events';
  if (cat === 'organizerFooter') return 'Organizer footer';
  if (cat === 'donate') return 'Fundraising';
  if (cat === 'audio') {
    return 'Audio';
  }
  if (cat === 'video') return 'Video';
  if (cat === 'divider') return 'Divider';
  if (cat === 'map') return 'Location map';
  if (cat === 'countdown') return 'Countdown';
  if (cat === 'weather') return 'Weather';
  if (cat === 'embed') return 'Embed';
  if (cat === 'image') {
    const layout = w.config?.layout ?? 'single';
    return layout === 'carousel' ? 'Image carousel' : layout === 'gallery' ? 'Image gallery' : 'Image';
  }
  const kind = textKindOf(w);
  return kind === 'richtext' ? 'Rich text' : kind === 'markdown' ? 'Markdown' : kind === 'html' ? 'HTML' : 'Text';
};

export const blockIcon = (w: PageWidget): string => {
  const cat = categoryOf(w);
  if (cat === 'links') return 'Link';
  if (cat === 'schedule') return 'CalendarEvent';
  if (cat === 'guests') return 'Users';
  if (cat === 'hosts') return 'UserStar';
  if (cat === 'highlights') return 'Award';
  if (cat === 'stats') return 'ChartBar';
  if (cat === 'tickets') return 'Ticket';
  if (cat === 'quote') return 'Quote';
  if (cat === 'cards') return 'LayoutGrid';
  if (cat === 'pois') return 'MapPin2';
  if (cat === 'accommodations') return 'BuildingSkyscraper';
  if (cat === 'signup') return 'ClipboardList';
  if (cat === 'poll') return 'MessageCircle';
  if (cat === 'tasks') return 'Checklist';
  if (cat === 'travel') return 'Plane';
  if (cat === 'budget') return 'Coin';
  if (cat === 'eventMap') return 'Map';
  if (cat === 'partnerPackages') return 'Package';
  if (cat === 'volunteerShifts') return 'HeartHandshake';
  if (cat === 'partners') return 'HeartHandshake';
  if (cat === 'artists') return 'Microphone2';
  if (cat === 'prizeWinners') return 'Trophy';
  if (cat === 'gameScoreboard') return 'Sparkles';
  if (cat === 'myRole') return 'DeviceGamepad2';
  if (cat === 'raceResults') return 'Trophy';
  if (cat === 'contactUs') return 'Mail';
  if (cat === 'lostFound') return 'Backpack';
  if (cat === 'guestbook') return 'MessageCircle';
  if (cat === 'share') return 'Share';
  if (cat === 'organizerEvents') return 'CalendarEvent';
  if (cat === 'organizerFooter') return 'Building';
  if (cat === 'donate') return 'Heart';
  if (cat === 'audio') {
    return 'Music';
  }
  if (cat === 'video') return 'PlayerPlay';
  if (cat === 'divider') return 'SeparatorHorizontal';
  if (cat === 'map') return 'MapPin';
  if (cat === 'countdown') return 'Clock';
  if (cat === 'weather') return 'CloudRain';
  if (cat === 'embed') return 'Frame';
  if (cat === 'image') return 'Photo';
  const kind = textKindOf(w);
  return kind === 'markdown' ? 'Markdown' : kind === 'html' ? 'Code' : kind === 'text' ? 'AlignLeft' : 'TextCaption';
};
